'use strict';

const { gql } = require('graphql-tag');
const momentTz = require('moment-timezone');

var _parent = {};

var outcomes = {
  init: p => (_parent = p),

  // -------------------------------- GQL FUNCTIONS -------------------------------- //

  /** Get necessary move outcome information from the database
   * @param {number} moveId - The move id
   */
  getMoveDetails: async moveId => {
    const res = await _parent.gql.query(
      gql`
        query sdk_outcomes_getMoveDetails($moveId: bigint!) {
          moves(where: { id: { _eq: $moveId } }) {
            config
            customer_id
            dealer_contact
            id
            move_details
            payer_id
            pickup_time
            reference_num
            sla_id
            status
            vehicle_color
            vehicle_image
            vehicle_make
            vehicle_model
            vehicle_odometer
            vehicle_stock
            vehicle_vin
            vehicle_year
            workflowset_id
            accountsReceivable {
              id
              due_amount
            }
            appayments(where: { type: { _eq: "move pay" } }) {
              id
              amount
            }
            childMoves(where: { move_type: { _eq: "ride" }, active: { _eq: 1 } }) {
              id
              pickup_time
              status
              appayments(where: { type: { _eq: "move pay" } }) {
                id
                amount
              }
              lane {
                id
                estimated_rideshare_return_cost
              }
              ride {
                id
                activeAttempt {
                  id
                  estimated_ride_cost
                  ride_cost
                }
              }
            }
            customer {
              id
              config
              organization {
                id
                config
              }
            }
            driverPayRate {
              pay_rate_rule_per_minute
            }
            hangtags {
              id
            }
            lane {
              pickup {
                id
                address
                latitude
                longitude
                name
                place_id
                timezone
              }
              delivery {
                id
                address
                latitude
                longitude
                name
                place_id
              }
            }
          }
        }
      `,
      { moveId }
    );
    return res;
  },

  /** Update the move with the outcome adjustments that were built
   ** So far, we just need to set the status and cancel_status
   * @param {number} moveId - The move id
   * @param {object} moveOutcome - The move outcome object to apply in gql
   */
  updateMoveWithAdjustments: async (moveId, moveOutcome) => {
    const res = await _parent.gql.mutation(
      gql`
        mutation sdk_outcomes_updateMoveWithAdjustments($moveId: bigint!, $moveOutcome: moves_set_input!) {
          update_moves(where: { id: { _eq: $moveId } }, _set: $moveOutcome) {
            affected_rows
            returning {
              id
              status
              cancel_status
            }
          }
        }
      `,
      {
        moveId,
        moveOutcome,
      }
    );
    return _parent.gql.injectAffectedRowsAtRoot(res);
  },

  updateRescheduledMove: async (moveId, newMoveId) => {
    const res = await _parent.gql.mutation(
      gql`
        mutation sdk_outcomes_updateRescheduledMove($moveId: bigint!, $newMoveId: bigint!) {
          update_moves(
            where: { id: { _eq: $moveId } }
            _set: {
              cancel_status: "rescheduled"
              cancel_reason: "Attempted but will try to rescheduled"
              parent_move_id: $newMoveId
            }
          ) {
            affected_rows
            returning {
              id
              cancel_status
              parent_move_id
            }
          }
        }
      `,
      {
        moveId,
        newMoveId,
      }
    );
    console.log(`Updated rescheduled move:`, res);
    return _parent.gql.injectAffectedRowsAtRoot(res);
  },

  unassignHangtag: async hangtagId => {
    const res = await _parent.gql.mutation(
      gql`
        mutation sdk_outcomes_unassignHangtag($hangtagId: bigint!) {
          update_hangtags(
            where: { id: { _eq: $hangtagId } }
            _set: { move_id: null, status: "unassigned", workflow_data: null }
          ) {
            affected_rows
            returning {
              id
              move_id
              status
              workflow_data
            }
          }
        }
      `,
      { hangtagId }
    );
    return _parent.gql.injectAffected;
  },

  updateRides: async rideIds => {
    const res = await _parent.gql.mutation(
      gql`
        mutation sdk_outcomes_updateRides($rideIds: [bigint!]!) {
          update_moves(where: { id: { _in: $rideIds } }, _set: { active: 0 }) {
            affected_rows
            returning {
              id
              cancel_status
            }
          }
        }
      `,
      {
        rideIds,
      }
    );
    return _parent.gql.injectAffectedRowsAtRoot(res);
  },

  // -------------------------------- HELPER FUNCTIONS -------------------------------- //

  /** Based on user input, set the outcome to be canceled */
  setCancelOutcome: (moveOutcome = {}, cancelOutcome, cancelReason) => {
    if (cancelOutcome) {
      // Check if move was canceled before or after it started
      if (cancelOutcome === `before`) {
        moveOutcome.cancel_status = `canceled`;

        // Canceled moves have certain additional fields that need to be set
        moveOutcome.move_failed = null;
        moveOutcome.chargeable = false;
        moveOutcome.payable = false;
        moveOutcome.canceled_at = 'now()';
      }
      if (cancelOutcome === `after`) {
        moveOutcome.cancel_status = `started`;

        // Canceled moves have certain additional fields that need to be set
        moveOutcome.move_failed = null;
        moveOutcome.chargeable = true;
        moveOutcome.payable = true;
        moveOutcome.canceled_at = 'now()';
      }

      // Set the cancel reason if it was provided
      if (cancelReason) moveOutcome.cancel_reason = cancelReason;
    }
    return moveOutcome;
  },

  /** Read yes or no from a string and parse it to a bool */
  parseYesOrNo: str => {
    if (!str || typeof str !== `string`) return null;
    if (str.toLowerCase() === `yes`) return true;
    if (str.toLowerCase() === `no`) return false;
    return null;
  },

  /** Read the customer config object and determine the price per minute rate */
  calcPricePerMinute: customerConfig => {
    let pricePerMinute = _parent.accessorials.DEFAULT_PRICE_PER_MINUTE;
    if (customerConfig && customerConfig.delay_price_per_minute)
      pricePerMinute = Number(customerConfig.delay_price_per_minute);
    return pricePerMinute;
  },

  /** Read the driverPayRate object and determine the pay per minute rate */
  calcPayPerMinute: driverPayRate => {
    let payPerMinute = _parent.accessorials.DEFAULT_PAY_PER_MINUTE;
    if (driverPayRate.pay_rate_rule_per_minute) payPerMinute = Number(driverPayRate.pay_rate_rule_per_minute);
    return payPerMinute;
  },

  createNewMove: async (move, customerId) => {
    const moveRes = await _parent.moves.createOneWayMove(move, customerId);
    return moveRes;
  },

  rescheduleMove: async (moveId, moveOutcome) => {
    try {
      const moveRes = await _parent.outcomes.getMoveDetails(moveId);
      const move = moveRes.data[0];
      const timezone = move.lane.pickup.timezone;

      console.log('Move for reschedule:', move);

      const readyByTime = momentTz.tz(moveOutcome.reschedule_date, timezone).utc().format('YYYY-MM-DD HH:mm:ss');
      const updateMoveTime = {
        ...move,
        ready_by: readyByTime,
      };
      
      const convertMove = await _parent.moves.convertMoveToAPISchema(updateMoveTime);
      console.log('Converted move:', convertMove);

      convertMove.move.sla_id = move.sla_id;
      convertMove.move.workflowset_id = move.workflowset_id;

      // Create a copy of move including ref, stock, and move config
      const newMove = await _parent.outcomes.createNewMove(convertMove, move.customer.id);
      console.log('Created new move:', newMove);

      // Set cancel status on original move to rescheduled
      // Set parent move id on original move to new move id
      const updateOldMove = await _parent.outcomes.updateRescheduledMove(moveId, newMove.move.id);
      console.log('Updated old move:', updateOldMove);

      if (move.hangtags && move.hangtags.length) {
        const unassignHangtag = await _parent.outcomes.unassignHangtag(move.hangtags[0].id);
        console.log('Unassigned hangtag:', unassignHangtag);
      }

      // Check if there are rides attached
      if (move.childMoves && move.childMoves.length) {
        console.log('There are rides attached:', move.childMoves);

        // Get all associated rides that are not called
        const incompleteAssociatedRides = move.childMoves.filter(r => r.status === null);
        console.log('Incomplete associated rides:', incompleteAssociatedRides);

        // Update incomplete associated rides
        if (incompleteAssociatedRides.length) {
          const updateRides = await _parent.outcomes.updateRides(incompleteAssociatedRides.map(r => r.id));
          console.log('Updated rides:', updateRides);
        }
      }
    } catch (err) {
      console.error(`Failed to reschedule move:`, err);
    }
  },

  // -------------------------------- ORCHESTRATION FUNCTIONS -------------------------------- //

  /** Build the move outcome based on a workflow output from the move outcome form
   * @param {number} moveId - The ID of the move to build the outcome from
   * @param {object} workflowOutput - The workflow output from the move outcome form
   * @example workflowOutput: {
      'move-outcome': 'breakdown',
      'breakdown-outcome': 'no',
      'roadside-outcome': 'yes',
      'roadside-customer-charge': 'yes',
      'roadside-outcome-input': '100',
      'roadside-delay': 'yes',
      'roadside-delay-input': '40',
      'tow-outcome': 'yes',
      'tow-customer-charge': 'yes',
      'tow-outcome-input': '100',
      'tow-delay': 'yes',
      'tow-delay-input': '30',
      'ride-outcome': 'yes',
      'ride-customer-charge': 'yes',
      'ride-delay': 'yes',
      'ride-delay-input': '20',
      'delay-outcome': 'yes',
      'delay-customer-charge': 'yes',
      'delay-outcome-input': '10'
    }
   * @returns {object} - The move outcome object
   */
  buildMoveOutcome: async (moveId, workflowOutput) => {
    try {
      // Validate the params
      if (!moveId) throw new Error(`Move ID is required!`);
      if (!workflowOutput) throw new Error(`Workflow output is required!`);

      // Get the move record from the move ID
      const moveRes = await _parent.outcomes.getMoveDetails(moveId);
      let move = moveRes.data[0];
      if (!move) throw new Error(`Move not found!`);

      // Find the ride after if it exists
      let rideAfter =
        (move.childMoves.length && move.childMoves.find(r => r.pickup_time > move.pickup_time)) || undefined;
      if (!rideAfter) console.log(`No ride after detected, continuing...`);

      // Get the enriched customer config
      const moveCustomerConfig = move.customer ? move.customer.config : {};
      const moveOrganizationConfig =
        move.customer && move.customer.organization ? move.customer.organization.config : {};
      const customerConfig = _parent.configs.enrichCustomerConfig(moveCustomerConfig, moveOrganizationConfig);

      // Calculate customer price per minute
      const pricePerMinute = _parent.outcomes.calcPricePerMinute(customerConfig);

      // Calculate driver pay per minute
      const payPerMinute = _parent.outcomes.calcPayPerMinute(move.driverPayRate);

      // Initialize te move outcome to update and return (uses the db move schema)
      let moveOutcome = { id: moveId };
      let accessorials = [];

      if (workflowOutput['move-outcome'] === 'reschedule') {
        Object.assign(moveOutcome, {
          reschedule_status: 'rescheduled',
          reschedule_reason: workflowOutput['reschedule-reason'],
          reschedule_date: workflowOutput['reschedule-date'],
        });
      }

      // Handle cancel outcome
      if (workflowOutput['move-outcome'] === `cancel`) {
        moveOutcome = _parent.outcomes.setCancelOutcome(
          moveOutcome,
          workflowOutput['cancel-outcome'],
          workflowOutput['cancel-reason']
        );
      }

      // Handle accident outcome
      if (workflowOutput['move-outcome'] === `accident`) {
        // If the move was still completed, we should mark it as so
        if (_parent.outcomes.parseYesOrNo(workflowOutput['accident-outcome']))
          moveOutcome.status = `delivery successful`;
        // Otherwise, treat it as canceled
        else
          moveOutcome = _parent.outcomes.setCancelOutcome(moveOutcome, `after`, `Vehicle was involved in an accident`);
      }

      // Handle breakdown outcome
      if (workflowOutput['move-outcome'] === `breakdown`) {
        // If the move was still completed, we should mark it as so
        if (_parent.outcomes.parseYesOrNo(workflowOutput['breakdown-outcome']))
          moveOutcome.status = `delivery successful`;
        // Otherwise, treat it as canceled
        else moveOutcome = _parent.outcomes.setCancelOutcome(moveOutcome, `after`, `Vehicle had a breakdown`);
      }

      // Handle partial accessorial
      if (moveOutcome.cancel_status === `started`) {
        const partialAccessorial = _parent.accessorials.buildPartialAccessorial({
          moveId,
          customerResponsible: true,
          moveStatus: move.status,
          movePay: move.appayments && move.appayments.length ? move.appayments[0].amount : 0,
          cancellationFee: customerConfig.cancellation_fee,
        });
        accessorials.push(partialAccessorial);
      }

      // Handle roadside accessorial
      if (_parent.outcomes.parseYesOrNo(workflowOutput['roadside-outcome'])) {
        const roadsideAccessorial = _parent.accessorials.buildRoadsideAccessorial({
          moveId,
          customerResponsible: _parent.outcomes.parseYesOrNo(workflowOutput['roadside-customer-charge']),
          roadsideCost: Number(workflowOutput['roadside-outcome-input']),
          driverIdleTimeMinutes: Number(workflowOutput['roadside-delay-input']),
          customerPricePerMinute: pricePerMinute,
          driverPayPerMinute: payPerMinute,
        });
        accessorials.push(roadsideAccessorial);
      }

      // Handle tow accessorial
      if (_parent.outcomes.parseYesOrNo(workflowOutput['tow-outcome'])) {
        const towAccessorial = _parent.accessorials.buildTowAccessorial({
          moveId,
          customerResponsible: _parent.outcomes.parseYesOrNo(workflowOutput['tow-customer-charge']),
          towCost: Number(workflowOutput['tow-outcome-input']),
          driverIdleTimeMinutes: Number(workflowOutput['tow-delay-input']),
          customerPricePerMinute: pricePerMinute,
          driverPayPerMinute: payPerMinute,
        });
        accessorials.push(towAccessorial);
      }

      // Handle ride accessorial
      if (_parent.outcomes.parseYesOrNo(workflowOutput['ride-outcome']) && rideAfter) {
        const actualRideCost = _parent.utilities.getPropValue(rideAfter, `ride.activeAttempt.ride_cost`);
        const estimatedRideCost = _parent.utilities.getPropValue(rideAfter, `ride.activeAttempt.estimated_ride_cost`);
        const laneRideCost = _parent.utilities.getPropValue(rideAfter, `lane.estimated_rideshare_return_cost`);

        const rideAccessorial = _parent.accessorials.buildRideAccessorial({
          moveId,
          customerResponsible: _parent.outcomes.parseYesOrNo(workflowOutput['ride-customer-charge']),
          rideCost: actualRideCost || estimatedRideCost || laneRideCost || 0,
          driverIdleTimeMinutes: Number(workflowOutput['ride-delay-input']),
          customerPricePerMinute: pricePerMinute,
          driverPayPerMinute: payPerMinute,
        });
        accessorials.push(rideAccessorial);
      }

      // Handle delay accessorial
      if (_parent.outcomes.parseYesOrNo(workflowOutput['delay-outcome'])) {
        const delayAccessorial = _parent.accessorials.buildDelayAccessorial({
          moveId,
          customerResponsible: _parent.outcomes.parseYesOrNo(workflowOutput['delay-customer-charge']),
          driverIdleTimeMinutes: Number(workflowOutput['delay-outcome-input']),
          customerPricePerMinute: pricePerMinute,
          driverPayPerMinute: payPerMinute,
        });
        accessorials.push(delayAccessorial);
      }

      // Remove nulls from accessorials
      accessorials = accessorials.filter(a => a);

      // Combine the outcome and accessorials
      moveOutcome.accessorials = accessorials;

      // Return the finalized move outcome
      console.log(`Successfully built move outcome:`, moveOutcome);
      return moveOutcome;
    } catch (err) {
      console.error(`Failed to build move outcome:`, err);
      throw new Error(`Failed to build move outcome!`);
    }
  },

  /** Persist the move outcome that was built by buildMoveOutcome
   ** This will persist the changes to the database
   * @param {number} moveId - The ID of the move to persist the outcome to
   * @param {object} moveOutcome - The move outcome object that was built by buildMoveOutcome
   * @returns {boolean} - True if the move outcome was applied successfully, false otherwise
   */
  persistMoveOutcome: async (moveId, moveOutcome) => {
    try {
      // Validate the params
      if (!moveId) throw new Error(`Move ID is required!`);
      if (!moveOutcome) throw new Error(`Move outcome is required!`);

      // Separate the move and accessorials and delete the accessorials from the move
      let move = { ...moveOutcome };
      let accessorials = [...move.accessorials];
      delete move.accessorials;

      // Initialize responses
      let moveRes;
      let accRes;

      if (moveOutcome.reschedule_status === 'rescheduled') {
        console.log(`Rescheduling move...`, moveOutcome);
        await _parent.outcomes.rescheduleMove(moveId, moveOutcome);
      }

      // If there are changes detected on the move, persist them
      if (move.status || move.cancel_status || move.cancel_reason) {
        moveRes = await _parent.outcomes.updateMoveWithAdjustments(moveId, move);
        if (!moveRes || !moveRes.success) throw new Error(`Failed to update move!`);
      }

      // If there are accessorials to add, persist them
      if (accessorials && accessorials.length) {
        accRes = await _parent.accessorials.create(accessorials);
        if (!accRes || !accRes.success) throw new Error(`Failed to insert accessorials!`);
      }

      // Return responses
      const res = { ...moveRes, accessorials: accRes };
      console.log(`Successfully persisted move outcome:`, res);
      return res;
    } catch (err) {
      console.error(`Failed to persist move outcome:`, err);
      throw new Error(`Failed to persist move outcome!`);
    }
  },
};

module.exports = outcomes;
